import React, { PureComponent } from 'react';
import type { WrappedComponentProps } from 'react-intl-next';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';
import { styled } from '@compiled/react';
import type { ApolloQueryResult } from 'apollo-client';

import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import AkButton from '@atlaskit/button/custom-theme-button';
import {
	DropdownItem as AkDropdownItem,
	DropdownItemGroup as AkDropdownItemGroup,
} from '@atlaskit/dropdown-menu';
import ShowMoreHorizontalIconMigration from '@atlaskit/icon/core/migration/show-more-horizontal--editor-more';
import { token } from '@atlaskit/tokens';
import AkTooltip from '@atlaskit/tooltip/Tooltip';
import { SpotlightTarget } from '@atlaskit/onboarding';

// Helpers / Constants
import { EDITOR2_SHORTCUTS, getShortcutString } from '@confluence/shortcuts';
import { EditLabelDropdownItem } from '@confluence/labels';
import { DialogsStateContainer } from '@confluence/dialogs';
import { useWebItemLocation } from '@confluence/web-item-location';
import {
	ResolvedInlineCommentsCounterLoader,
	ResolvedInlineCommentsDialogLoader,
} from '@confluence/resolved-inline-comments-dialog';
import { ViewChangesDialogLoader } from '@confluence/view-changes-dialog';
import { EditorMovePageButtonLoader } from '@confluence/editor-move-page-button';
import { getApolloClient, markErrorAsHandled } from '@confluence/graphql';
import { isUnauthorizedError, Attribution } from '@confluence/error-boundary';
import { containsNoNetworkError } from '@confluence/network';
import { getMonitoringClient } from '@confluence/monitoring';
import { RevertEditorDialog } from '@confluence/editor-conversion/entry-points/RevertEditorDialog';
import { getAutoConvertionInfo } from '@confluence/editor-conversion';
import type { ContentPropertiesQueryType } from '@confluence/content-body/entry-points/ContentPropertiesQuery';
import { ContentPropertiesQuery } from '@confluence/content-body/entry-points/ContentPropertiesQuery';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState';
import { LoadableLazy } from '@confluence/loadable';
import { VIEW_PAGE } from '@confluence/named-routes';
import { useSpaceId } from '@confluence/space-utils';
import { ConvertToLivePageDialog } from '@confluence/live-pages-features';
import { convertDraftToLivePage } from '@confluence/live-pages-features/entry-points/convertDraftToLivePage';
import { isLivePagesFeatureEnabled } from '@confluence/live-pages-utils/entry-points/isLivePagesFeatureEnabled';
import {
	useIsEllipsisClassifyEnabled,
	useIsDataClassificationEnabledForOrg,
	EllipsisClassificationMenuItem,
} from '@confluence/data-classification/entry-points/ellipsisClassification';
import { fg } from '@confluence/feature-gating';
import { UNSAFE_noExposureExp } from '@confluence/feature-experiments';
import { withFlags } from '@confluence/flags';
import type { RetrieveEditorTitleAndContent } from '@confluence/publish-page-as-blog';

import { Buttons } from './types';
import type { EllipsisDropdownComponentProps } from './types';
import { onClickPageRevert } from './helpers';
import { CommonDropdownMenu } from './CommonDropdownMenu';
import { useCompanyHubSettingsGroupMenuItems } from './useCompanyHubSettingsGroupMenuItems';

export const i18n = defineMessages({
	preview: {
		id: 'editor-ellipsis-dropdown.preview',
		description: 'The label of Preview button inside ellipsis dropdown of the editor',
		defaultMessage: 'Preview',
	},
	templateImportDocs: {
		id: 'editor-ellipsis-dropdown.ellipsis.open.ellipsis.templates.and.import',
		description:
			'The label of Templates & import doc button inside ellipsis dropdown of the editor',
		defaultMessage: 'Templates & import doc',
	},
	templateDocs: {
		id: 'editor-ellipsis-dropdown.ellipsis.open.ellipsis.templates',
		description: 'The label of Templates button inside ellipsis dropdown of the editor',
		defaultMessage: 'Templates',
	},
	addLabels: {
		id: 'editor-ellipsis-dropdown.open.ellipsis.edit.labels',
		description: 'The label of Add labels button inside ellipsis dropdown of the editor',
		defaultMessage: 'Add labels',
	},
	resolvedComments: {
		id: 'editor-ellipsis-dropdown.open.ellipsis.resolved.comments',
		description: 'The label of Resolved comments button inside ellipsis dropdown of the editor',
		defaultMessage: 'Resolved comments',
	},
	deleteUnpublishedPage: {
		id: 'editor-ellipsis-dropdown.open.ellipsis.page.delete',
		description:
			'The label of Delete unpublished page button inside ellipsis dropdown of the editor',
		defaultMessage: 'Delete unpublished page',
	},
	deleteUnpublishedBlogpost: {
		id: 'editor-ellipsis-dropdown.open.ellipsis.blogpost.delete',
		description:
			'The label of Delete unpublished blog post button inside ellipsis dropdown of the editor',
		defaultMessage: 'Delete unpublished blog post',
	},
	revert: {
		id: 'editor-ellipsis-dropdown.open.ellipsis.revert',
		description:
			'The label of Delete unpublished blog post button inside ellipsis dropdown of the editor',
		defaultMessage: 'Revert to previous version',
	},
	updateWithoutNotifying: {
		id: 'editor-ellipsis-dropdown.open.ellipsis.update.without.notify',
		description:
			'The label of Update without notifying watchers button inside ellipsis dropdown of the editor',
		defaultMessage: 'Update without notifying watchers',
	},
	moreActions: {
		id: 'editor-ellipsis-dropdown.open.ellipsis.more.actions.tooltip',
		defaultMessage: 'More actions',
	},
	viewChanges: {
		id: 'editor-ellipsis-dropdown.open.ellipsis.view.changes',
		description: 'The label of View changes button inside ellipsis dropdown of the editor',
		defaultMessage: 'View changes',
	},
	more: {
		id: 'editor-ellipsis-dropdown.open.ellipsis.more',
		description: 'The label of the More icon',
		defaultMessage: 'More',
	},
	openTemplateBrowser: {
		id: 'editor-ellipsis-dropdown.open.template.browser',
		defaultMessage: 'Open template browser',
	},
	revertEditor: {
		id: 'editor-ellipsis-dropdown.open.ellipsis.revert.editor',
		description: 'The label to revert the page from Fabric to Tiny',
		defaultMessage: 'Revert to legacy editor',
	},
});

const LivePagesExpandedEapConversionSpotlightLoader = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-LivePagesExpandedEapConversionSpotlight" */ './LivePagesExpandedEapConversionSpotlight'
			)
		).LivePagesExpandedEapConversionSpotlight,
});

const noop = () => {};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MenuItemContainer = styled.span({
	display: 'flex',
	justifyContent: 'space-between',
});

const previewPage = (createAnalyticsEvent, onPreviewClicked, contentId, contentType) => {
	createAnalyticsEvent({
		type: 'sendUIEvent',
		data: {
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'previewPage',
			source: 'ellipsisDropdownComponent',
			objectId: contentId,
			objectType: contentType,
		},
	}).fire();

	onPreviewClicked(false);
};

const publishWithoutNotify = (createAnalyticsEvent, onClickPublish, contentId, contentType) => {
	createAnalyticsEvent({
		type: 'sendUIEvent',
		data: {
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'publishWithoutNotifyFromOverflow',
			source: 'ellipsisDropdownComponent',
			objectId: contentId,
			objectType: contentType,
		},
	}).fire();

	onClickPublish({ suppressNotifyWatchers: true })();
};

class EllipsisDropdownClass extends PureComponent<
	EllipsisDropdownComponentProps &
		WithAnalyticsEventsProps &
		WrappedComponentProps & {
			addCompanyHubSettingsGroupMenuItems: ReturnType<typeof useCompanyHubSettingsGroupMenuItems>;
			isLivePagesEnabled: boolean;
			isClassificationEnabled: boolean;
			push: (url: string) => void;
			isInitialMenuOpen: boolean;
		},
	{
		isMenuOpen: boolean;
		isPageAutoconverted: boolean;
	}
> {
	context: any;
	static displayName = 'EllipsisDropdown';

	static defaultProps = {
		saveDraft: noop,
		isDisabled: false,
		isInitialMenuOpen: false,
	};

	state = {
		isMenuOpen: this.props.isInitialMenuOpen,
		isPageAutoconverted: false,
	};

	componentDidMount() {
		void this.setEditorProperty();
	}

	/**
	 * Set editor property state
	 * @returns {Promise<void>}
	 */
	setEditorProperty = async (): Promise<void> => {
		const { contentId } = this.props;
		const {
			data: contentPropertiesData,
		}: ApolloQueryResult<ContentPropertiesQueryType> | { data: null } = await getApolloClient()
			.query({
				query: ContentPropertiesQuery,
				variables: { contentId },
			})
			.catch((e) => {
				if (isUnauthorizedError(e) || containsNoNetworkError(e)) {
					markErrorAsHandled(e);
				} else {
					getMonitoringClient().submitError(e, {
						attribution: Attribution.BACKBONE,
					});
				}
				return { data: null };
			});

		if (contentPropertiesData) {
			const { editorProperty } = getAutoConvertionInfo(contentPropertiesData?.content?.nodes?.[0]);
			const isNewPage = contentPropertiesData?.content?.nodes?.length === 0;

			this.setState({
				isPageAutoconverted: !editorProperty && !isNewPage,
			});
		}
	};

	saveDraftAnd = (afterSaveDraftDispatched) => () => {
		if (this.props.saveDraft) {
			void this.props.saveDraft();
		}
		afterSaveDraftDispatched();
	};

	// when allowed ellipsis buttons is undefined, enable all buttons
	isAllowed = (menuItem: Buttons) => this.props.allowedEllipsisButtons?.includes(menuItem) ?? true;

	handleMenuOpenChange = (attrs) => this.setState({ isMenuOpen: attrs.isOpen });
	/**
	 * Menu items are pushed into the 'items' list which is used to create the '•••' menu
	 * (aka more aka ellipsis aka meatball) for editor pages
	 */
	_getItems(dialogs) {
		const {
			addCompanyHubSettingsGroupMenuItems,
			contentType,
			isDisabled,
			isUnpublishedDraft,
			showRevertDialog,
			spaceKey,
			spaceId,
			contentId,
			isContentCreationPanelShown,
			onClickOpenContentCreationPanel,
			onClickPublish,
			retrieveEditorTitleAndContent,
			onPreviewClicked,
			createAnalyticsEvent,
			isImportDisabled,
			push,
			flags,
			saveDraft,
		} = this.props;

		if (this.state.isMenuOpen) {
			createAnalyticsEvent?.({
				type: 'sendScreenEvent',
				data: {
					name: 'ellipsisDropdownForMoreItems',
				},
			}).fire();
		}

		// Don't want to trigger exposures when users open the ellipsis dropdown we are
		// just disabling an item that is not required for the experiment
		// The exposure will happen when the user converts a page instead
		const [onEditConvertExp] = UNSAFE_noExposureExp('platform_editor_on_edit_convert');

		const addPreviewMenuItem = (menuItems: JSX.Element[]) => {
			menuItems.push(
				<AkDropdownItem
					key="preview"
					onClick={() => {
						previewPage(createAnalyticsEvent, onPreviewClicked, contentId, contentType);
					}}
					elemAfter={
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						<span style={{ color: token('color.icon.subtle') }}>
							{getShortcutString(EDITOR2_SHORTCUTS.PREVIEW)}
						</span>
					}
				>
					<FormattedMessage {...i18n.preview} />
				</AkDropdownItem>,
			);
		};

		const showViewChangesDialog = () =>
			dialogs.showDialog(ViewChangesDialogLoader, {
				contentId,
			});

		const addViewChangesMenuItem = (menuItems: JSX.Element[]) => {
			menuItems.push(
				<AkDropdownItem key="view-changes" onClick={this.saveDraftAnd(showViewChangesDialog)}>
					<FormattedMessage {...i18n.viewChanges} />
				</AkDropdownItem>,
			);
		};

		const addTemplateBrowserMenuItem = (menuItems: JSX.Element[]) => {
			const label = isImportDisabled ? i18n.templateDocs : i18n.templateImportDocs;
			menuItems.push(
				<AkDropdownItem key="open-template-browser" onClick={onClickOpenContentCreationPanel}>
					<FormattedMessage {...label} />
				</AkDropdownItem>,
			);
		};

		const addClassifyContentMenuItem = (menuItems: JSX.Element[]) => {
			if (this.props.isClassificationEnabled) {
				menuItems.push(<EllipsisClassificationMenuItem />);
			}
		};

		const addEditLabelsMenuItem = (menuItems: JSX.Element[]) => {
			menuItems.push(
				<EditLabelDropdownItem contentId={contentId || ''} spaceKey={spaceKey} dialogs={dialogs} />,
			);
		};

		const addMovePageMenuItem = (menuItems, movePageContentId, movePageSpaceKey) => {
			menuItems.push(
				<EditorMovePageButtonLoader
					contentId={movePageContentId}
					spaceKey={movePageSpaceKey}
					appearance="dropdownItem"
					analyticsSource="EllipsisDropdownComponent"
					key="move-page-button"
					retrieveEditorTitleAndContent={retrieveEditorTitleAndContent}
					isUnpublishedDraft={!!isUnpublishedDraft}
				/>,
			);
		};

		const addResolvedCommentsMenuItem = (menuItems: JSX.Element[], contentId, spaceKey) => {
			menuItems.push(
				<AkDropdownItem
					key="resolved-comments"
					onClick={() => {
						dialogs.showDialog(ResolvedInlineCommentsDialogLoader, {
							spaceKey,
							contentId,
							context: 'editor',
						});
					}}
				>
					<MenuItemContainer>
						<FormattedMessage {...i18n.resolvedComments} />{' '}
						<ResolvedInlineCommentsCounterLoader contentId={contentId} />
					</MenuItemContainer>
				</AkDropdownItem>,
			);
		};

		const addDeleteChangesMenuItem = (menuItems: JSX.Element[]) => {
			menuItems.push(
				<AkDropdownItem key="delete-changes" onClick={this.saveDraftAnd(showRevertDialog)}>
					{isUnpublishedDraft && contentType === 'page' ? (
						<FormattedMessage {...i18n.deleteUnpublishedPage} />
					) : null}
					{isUnpublishedDraft && contentType !== 'page' ? (
						<FormattedMessage {...i18n.deleteUnpublishedBlogpost} />
					) : null}
					{!isUnpublishedDraft ? <FormattedMessage {...i18n.revert} /> : null}
				</AkDropdownItem>,
			);
		};

		const addSwitchToLiveEditMenuItem = ({
			menuItems,
			contentId,
			isUnpublishedDraft,
			saveDraft,
			retrieveEditorTitleAndContent,
		}:
			| {
					menuItems: JSX.Element[];
					contentId: string;
					isUnpublishedDraft: true;
					saveDraft: (arg?: any) => Promise<void>;
					retrieveEditorTitleAndContent: RetrieveEditorTitleAndContent;
			  }
			| {
					menuItems: JSX.Element[];
					contentId: string;
					isUnpublishedDraft: false;
					saveDraft?: never;
					retrieveEditorTitleAndContent?: never;
			  }) => {
			const handleConvertToLivePage = () => {
				createAnalyticsEvent?.({
					type: 'sendUIEvent',
					data: {
						action: 'convertedToLivePage',
						actionSubject: 'page',
						source: 'ellipsisDropdownComponent',
						objectId: contentId,
						objectType: 'page',
						containerId: spaceId,
						containerType: 'space',
						attributes: {
							isDraft: isUnpublishedDraft,
						},
					},
				}).fire();

				const redirectLink = VIEW_PAGE.toUrl({
					spaceKey,
					contentId,
				});
				push(redirectLink);
			};

			const handleItemClicked = async () => {
				createAnalyticsEvent?.({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'switchToLiveEdit',
						source: 'ellipsisDropdownComponent',
						objectId: contentId,
						objectType: 'page',
						containerId: spaceId,
						containerType: 'space',
						attributes: {
							isDraft: isUnpublishedDraft,
						},
					},
				}).fire();

				if (!isUnpublishedDraft) {
					dialogs.showModal(ConvertToLivePageDialog, {
						contentId,
						onConfirm: handleConvertToLivePage,
						adfOrigin: 'EDITOR',
					});
				} else {
					// Trigger draft sync before converting. If we don't do this and the user recently updated their draft title then quickly converted the draft, draft sync may not have triggered and we may not identify their title conflict.
					await saveDraft();
					void convertDraftToLivePage({
						contentId,
						onSuccess: handleConvertToLivePage,
						flags,
						retrieveEditorTitleAndContent,
					});
				}
			};

			if (this.props.isLivePagesEnabled && contentType === 'page') {
				const switchToLiveEditWebItem = this.props.webItems?.find(
					(item) => item.id === 'action-switch-to-live-edit',
				);
				if (!switchToLiveEditWebItem) return;

				menuItems.push(
					<AkDropdownItem key="switch-to-live-edit" onClick={handleItemClicked}>
						{switchToLiveEditWebItem.label}
					</AkDropdownItem>,
				);
			}
		};

		const addPublishGroupMenuItems = (menuItems: JSX.Element[]) => {
			const publishGroupItems = [] as JSX.Element[];

			if (this.isAllowed(Buttons.SWITCH_TO_LIVE_EDIT) && contentId) {
				if (!isUnpublishedDraft) {
					addSwitchToLiveEditMenuItem({
						menuItems: publishGroupItems,
						contentId,
						isUnpublishedDraft: false,
					});
				} else if (saveDraft && fg('confluence_live_pages_switch_draft_to_live_page')) {
					addSwitchToLiveEditMenuItem({
						menuItems: publishGroupItems,
						contentId,
						isUnpublishedDraft: true,
						saveDraft,
						retrieveEditorTitleAndContent,
					});
				}
			}
			if (!isUnpublishedDraft) {
				this.isAllowed(Buttons.PUBLISH_WITHOUT_NOTIFY) &&
					publishGroupItems.push(
						<AkDropdownItem
							key="publish-without-notify"
							isDisabled={isDisabled}
							onClick={() => {
								publishWithoutNotify(createAnalyticsEvent, onClickPublish, contentId, contentType);
							}}
						>
							<FormattedMessage {...i18n.updateWithoutNotifying} />
						</AkDropdownItem>,
					);
			}

			publishGroupItems.length !== 0 &&
				menuItems.push(
					<div data-testid="publish-group-items" key="publish-group-items">
						<AkDropdownItemGroup>{publishGroupItems}</AkDropdownItemGroup>
					</div>,
				);
			return publishGroupItems.length;
		};

		const addOtherGroupMenuItems = (menuItems: JSX.Element[]) => {
			const otherGroupItems = [] as JSX.Element[];
			this.isAllowed(Buttons.PREVIEW) && addPreviewMenuItem(otherGroupItems);
			if (!isUnpublishedDraft && this.isAllowed(Buttons.VIEW_CHANGES)) {
				addViewChangesMenuItem(otherGroupItems);
			}
			// Show option to expose the page template menu that pops out from the side
			if (shouldShowOpenContentCreationMenuItem && this.isAllowed(Buttons.TEMPLATE_BROWSER)) {
				addTemplateBrowserMenuItem(otherGroupItems);
			}
			if (contentId && spaceKey) {
				addClassifyContentMenuItem(otherGroupItems);
				this.isAllowed(Buttons.EDIT_LABELS) && addEditLabelsMenuItem(otherGroupItems);
				this.isAllowed(Buttons.MOVE_PAGE) &&
					addMovePageMenuItem(otherGroupItems, contentId, spaceKey);
				this.isAllowed(Buttons.RESOLVED_COMMENTS) &&
					addResolvedCommentsMenuItem(otherGroupItems, contentId, spaceKey);
			}

			// Empty title needed to maintain blank line above group
			otherGroupItems.length !== 0 &&
				menuItems.push(
					<div data-testid="other-group-items" key="other-group-items">
						<AkDropdownItemGroup hasSeparator={menuItems.length >= 1}>
							{otherGroupItems}
						</AkDropdownItemGroup>
					</div>,
				);
		};

		const showRevertEditorDialog = () => {
			dialogs.showDialog(RevertEditorDialog, {
				contentId,
				onPageRevert: () => onClickPageRevert(push, contentType, spaceKey, contentId),
			});
		};

		const addRevertToLegacyEditor = (menuItems: JSX.Element[]) => {
			menuItems.push(
				<AkDropdownItem key="revert-editor" onClick={this.saveDraftAnd(showRevertEditorDialog)}>
					<FormattedMessage {...i18n.revertEditor} />
				</AkDropdownItem>,
			);
		};

		const addDiscardGroupMenuItems = (menuItems: JSX.Element[]) => {
			const discardGroupItems = [] as JSX.Element[];

			if (this.isAllowed(Buttons.DELETE_DRAFT)) {
				addDeleteChangesMenuItem(discardGroupItems);
				this.state.isPageAutoconverted &&
					!onEditConvertExp.get('isEnabled', false) &&
					addRevertToLegacyEditor(discardGroupItems);
			}

			// Empty title needed to maintain blank line above group
			discardGroupItems.length !== 0 &&
				menuItems.push(
					<div data-testid="discard-group-items" key="discard-group-items">
						<AkDropdownItemGroup hasSeparator={menuItems.length >= 1}>
							{discardGroupItems}
						</AkDropdownItemGroup>
					</div>,
				);
		};

		const shouldShowOpenContentCreationMenuItem =
			!isContentCreationPanelShown && isUnpublishedDraft && contentType === 'page';

		const menuItems = [] as JSX.Element[];

		/**
		 * Menu items are separated into three groups: publish, other and discard
		 * Use the allowedEllipsisButtons array to filter for buttons needed to be rendered
		 */

		addPublishGroupMenuItems(menuItems);
		addOtherGroupMenuItems(menuItems);
		addDiscardGroupMenuItems(menuItems);
		addCompanyHubSettingsGroupMenuItems(menuItems);

		return menuItems;
	}

	render() {
		const {
			intl,
			isDisabled,
			tooltipDisabledState,
			isLivePagesEnabled,
			contentType,
			isUnpublishedDraft,
		} = this.props;

		const label = intl.formatMessage(i18n.more);
		const moreIcon = <ShowMoreHorizontalIconMigration label={label} />;

		if (isDisabled) {
			const i18nDisabledTooltipMessage = { id: tooltipDisabledState };
			const disabledTooltipText = tooltipDisabledState
				? intl.formatMessage(i18nDisabledTooltipMessage)
				: '';

			return (
				<AkTooltip content={disabledTooltipText} position="bottom">
					<AkButton appearance="subtle" isDisabled iconBefore={moreIcon} />
				</AkTooltip>
			);
		}

		return (
			<Subscribe to={[DialogsStateContainer]}>
				{(dialogs) => (
					<>
						{isLivePagesEnabled && contentType === 'page' && !isUnpublishedDraft && (
							<LivePagesExpandedEapConversionSpotlightLoader />
						)}
						<SpotlightTarget name="live-pages-expanded-eap-changeboarding-convert-to-live">
							<div data-testid="ellipsis-menu">
								<CommonDropdownMenu
									isMenuOpen={this.state.isMenuOpen}
									handleMenuOpenChange={this.handleMenuOpenChange}
								>
									{this._getItems(dialogs)}
								</CommonDropdownMenu>
							</div>
						</SpotlightTarget>
					</>
				)}
			</Subscribe>
		);
	}
}

export const EllipsisDropdownComponent = withFlags((props: EllipsisDropdownComponentProps) => {
	const addCompanyHubSettingsGroupMenuItems = useCompanyHubSettingsGroupMenuItems(props);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intl = useIntl();
	const { isEnabled: isDataClassificationEnabled } = useIsDataClassificationEnabledForOrg();
	const { isEllipsisClassifyEnabled } = useIsEllipsisClassifyEnabled();
	const isClassificationEnabled = isDataClassificationEnabled && isEllipsisClassifyEnabled;

	const { push } = useRouteActions();
	const spaceId = useSpaceId();
	const webItemLocation = useWebItemLocation({
		location: 'system.content.action',
		contentId: props.contentId,
	});

	return (
		<EllipsisDropdownClass
			{...props}
			addCompanyHubSettingsGroupMenuItems={addCompanyHubSettingsGroupMenuItems}
			createAnalyticsEvent={createAnalyticsEvent}
			intl={intl}
			isLivePagesEnabled={isLivePagesFeatureEnabled()}
			isClassificationEnabled={isClassificationEnabled}
			push={push}
			spaceId={spaceId}
			webItems={webItemLocation?.webItems || []}
		/>
	);
});
